import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add,
  Check,
  Delete,
  DriveFileRenameOutline,
  Groups,
  PhotoLibrary,
  Publish,
  WarningAmber
} from '@mui/icons-material';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import TextInput from 'src/components/UI/input/TextInput';

// SAVED VIEWS selector (Phase 2 of PLAN-pedigree-undo-redo-and-saved-views,
// v1.1 §3.8; placement per decision 6 = the record header). Lists the synthetic
// "Standard" entry (the clinician's own layout), the caller's private views and
// the group's shared views; supports save-current-as, rename, share-with-group,
// delete, and the explicit "Update view" that is the ONLY way a group view's
// content changes (decision 8 — group views apply read-only).
//
// Role gating here is COSMETIC ONLY — the server enforces scope rules
// (pedigreeViewController): students get list/apply, group-share needs a
// genetics-service role.

const GROUP_SHARE_ROLES = ['admin', 'geneticsServiceAdmin', 'genetics'];

const STALE_TOOLTIP =
  'Saved before this family was re-imported — its layout may no longer match ' +
  'the people. Open it and use "Update view" to re-capture.';

function ViewsMenu({
  views,
  activeView,
  userType,
  isLocked = false,
  accent = '#2ba9bf',
  onApplyView,
  onApplyStandard,
  onSaveAs,
  onRename,
  onShare,
  onDelete,
  onUpdateLayout
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  // dialog: { mode: 'save' | 'rename' | 'delete', view? } | null
  const [dialog, setDialog] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [shareToGroup, setShareToGroup] = useState(false);
  const [nameError, setNameError] = useState(false);

  // Write actions pause while ANOTHER user holds the family edit lock: view
  // docs are the caller's own, but save/update would snapshot a colleague's
  // half-finished arrangement (browsing + applying stays available).
  const canWrite =
    userType !== 'student' && userType !== 'patient' && !isLocked;
  const canGroupShare = GROUP_SHARE_ROLES.includes(userType);

  const closeMenu = () => setAnchorEl(null);

  const openDialog = (mode, view = null) => {
    closeMenu();
    setNameInput(mode === 'rename' ? view.name : '');
    setShareToGroup(false);
    setNameError(false);
    setDialog({ mode, view });
  };

  const submitDialog = async () => {
    if (dialog.mode === 'delete') {
      await onDelete(dialog.view);
      setDialog(null);
      return;
    }
    const name = nameInput.trim();
    if (!name || name.toLowerCase() === 'standard') {
      setNameError(true);
      return;
    }
    const ok =
      dialog.mode === 'save'
        ? await onSaveAs({ name, scope: shareToGroup ? 'group' : 'user' })
        : await onRename(dialog.view, name);
    if (ok) setDialog(null);
  };

  const renderViewItem = (view, isGroup) => {
    const isActive = activeView?._id === view._id;
    return (
      <MenuItem
        key={view._id}
        selected={isActive}
        onClick={() => {
          closeMenu();
          if (!isActive) onApplyView(view);
        }}
        sx={{ pr: 1 }}
      >
        <ListItemIcon sx={{ minWidth: 30 }}>
          {isActive ? (
            <Check fontSize="small" sx={{ color: accent }} />
          ) : isGroup ? (
            <Groups fontSize="small" sx={{ color: '#8a94a3' }} />
          ) : null}
        </ListItemIcon>
        <ListItemText
          primary={view.name}
          sx={{ mr: 1, maxWidth: 220 }}
          slotProps={{
            primary: { fontSize: 14, noWrap: true }
          }}
        />
        {view.staleAt && (
          <Tooltip title={STALE_TOOLTIP}>
            <WarningAmber fontSize="small" sx={{ color: '#ed6c02', mr: 0.5 }} />
          </Tooltip>
        )}
        {canWrite && (
          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            <Tooltip title="Rename view">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openDialog('rename', view);
                }}
              >
                <DriveFileRenameOutline sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {!isGroup && canGroupShare && (
              <Tooltip title="Share with group — colleagues will see this view">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeMenu();
                    onShare(view);
                  }}
                >
                  <Groups sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Delete view">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openDialog('delete', view);
                }}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </MenuItem>
    );
  };

  const activeIsGroup = activeView?.scope === 'group';

  return (
    <>
      {/* Icon-only trigger — the header panel is a fixed 350px, so a text
          button with the view name would crush the family-ID headline. The
          active view name lives in the tooltip and the menu's tick mark. */}
      <Tooltip
        title={
          activeView
            ? `Pedigree view: ${activeView.name}${
                activeIsGroup ? ' (shared, read-only)' : ''
              }`
            : 'Pedigree views — named alternate layouts of this family'
        }
      >
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <PhotoLibrary
            fontSize="small"
            sx={{ color: activeView ? accent : '#bdbdbd' }}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closeMenu}
        slotProps={{ paper: { sx: { minWidth: 280 } } }}
      >
        <MenuItem
          selected={!activeView}
          onClick={() => {
            closeMenu();
            onApplyStandard();
          }}
        >
          <ListItemIcon sx={{ minWidth: 30 }}>
            {!activeView && <Check fontSize="small" sx={{ color: accent }} />}
          </ListItemIcon>
          <ListItemText
            primary="Standard"
            secondary="Your working layout"
            slotProps={{
              primary: { fontSize: 14 },
              secondary: { fontSize: 11 }
            }}
          />
        </MenuItem>
        {views.user.length > 0 && <Divider textAlign="left">My views</Divider>}
        {views.user.map((v) => renderViewItem(v, false))}
        {views.group.length > 0 && (
          <Divider textAlign="left">Shared with group</Divider>
        )}
        {views.group.map((v) => renderViewItem(v, true))}
        <Divider />
        {activeView && canWrite && (activeIsGroup ? canGroupShare : true) && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onUpdateLayout(activeView);
            }}
          >
            <ListItemIcon sx={{ minWidth: 30 }}>
              <Publish fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Update view"
              secondary={
                activeIsGroup
                  ? 'Save the current layout into this shared view'
                  : 'Re-capture the current layout into this view'
              }
              slotProps={{
                primary: { fontSize: 14 },
                secondary: { fontSize: 11 }
              }}
            />
          </MenuItem>
        )}
        {canWrite && (
          <MenuItem onClick={() => openDialog('save')}>
            <ListItemIcon sx={{ minWidth: 30 }}>
              <Add fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Save current as view…"
              slotProps={{
                primary: { fontSize: 14 }
              }}
            />
          </MenuItem>
        )}
      </Menu>
      {dialog && (
        <CustomDialog
          open={true}
          handleClose={() => setDialog(null)}
          showActions={true}
          saveBtnLabel={
            dialog.mode === 'save'
              ? 'Save view'
              : dialog.mode === 'rename'
              ? 'Rename'
              : 'Delete view'
          }
          cancelBtnLabel="Cancel"
          title={
            dialog.mode === 'save'
              ? 'Save current layout as a view'
              : dialog.mode === 'rename'
              ? 'Rename view'
              : 'Delete view?'
          }
          titleAppearance={dialog.mode === 'delete' ? 'warning' : undefined}
          minHeight={160}
          onSave={submitDialog}
          allowBackgroundClose={() => setDialog(null)}
        >
          <Box
            className="user-form"
            sx={{
              pt: 1
            }}
          >
            {dialog.mode === 'delete' && (
              <Typography
                sx={{
                  fontSize: 14
                }}
              >
                {`Delete the view "${dialog.view.name}"? Its arrangement and
                hidden set cannot be recovered.`}
                {dialog.view.scope === 'group' &&
                  ' This view is shared — colleagues will lose it too.'}
              </Typography>
            )}
            {dialog.mode === 'save' && (
              <Typography
                sx={{
                  pb: 1.5,
                  fontSize: 13
                }}
              >
                A view is a named alternate arrangement of the same family —
                applying or editing it never changes your Standard layout or the
                family records.
              </Typography>
            )}
            {dialog.mode !== 'delete' && (
              <TextInput
                label="View name"
                value={nameInput}
                error={nameError}
                helperText={
                  nameError &&
                  'A unique name is required ("Standard" is reserved)'
                }
                onChange={(e) => {
                  setNameInput(e.target.value);
                  setNameError(false);
                }}
              />
            )}
            {dialog.mode === 'save' && canGroupShare && (
              <FormControlLabel
                sx={{ pt: 1 }}
                control={
                  <Checkbox
                    size="small"
                    checked={shareToGroup}
                    onChange={(e) => setShareToGroup(e.target.checked)}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: 13
                    }}
                  >
                    Share with group (colleagues can apply it; it only changes
                    when someone explicitly updates it)
                  </Typography>
                }
              />
            )}
          </Box>
        </CustomDialog>
      )}
    </>
  );
}

export default ViewsMenu;
